import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  FieldType,
} from '@bybeam/platform-types';
import { formatIfPhone } from '@platform-ui-common/utilities/format.js';
import {
  AgreementOptions,
  ConfidentOptions,
  LikelyOptions,
  SatisfiedOptions,
  YesNoOptions,
} from '@platform-ui/application/utils/index.js';
import { eq, or } from '../../ast/utils.js';
import {
  COMMON_OVERRIDES,
  COMMON_PAGES,
  COMMON_QUESTIONS,
  COMMON_SECTIONS,
  VERIFICATION_FORM_LINK,
} from './common.js';

const QUESTIONS: {
  [key in
    | 'workInAnEligibleJob'
    | 'attestationOfHavingAJob'
    | 'attestationToSeekAJob'
    | 'licenses'
    | 'proofOfEligibilityType'
    | 'employers'
    | 'certifications'
    | 'surveyLevel1Group1'
    | 'surveyLevel1Group2'
    | 'surveyLevel1Group3'
    | 'surveyLevel1HEJGroup3'
    | 'surveyLevel2Group1'
    | 'surveyLevel2Group2'
    | 'surveyLevel2Group3'
    | 'surveyLevel2HEJGroup1'
    | 'surveyLevel2HEJGroup2'
    | 'surveyLevel2HEJGroup3'
    | 'surveyLevel2HEJGroup4'
    | 'surveyLevel3Group1'
    | 'surveyLevel3Group2'
    | 'surveyLevel3Group3'
    | 'surveyLevel3HEJGroup1'
    | 'surveyLevel3HEJGroup2'
    | 'surveyLevel3HEJGroup3'
    | 'surveyLevel3HEJGroup4']: ApplicationQuestion;
} = {
  workInAnEligibleJob: {
    key: 'workInAnEligibleJob',
    copy: {
      title: 'Do you currently work or volunteer under supervision in an eligible job setting?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'workInAnEligibleJob',
        options: YesNoOptions,
      },
    ],
  },
  attestationOfHavingAJob: {
    key: 'attestationOfHavingAJob',
    copy: { title: 'Attestation' },
    fields: [
      {
        key: 'attestationOfHavingAJob',
        type: FieldType.Typography,
        copy: 'By tapping CONTINUE, I acknowledge that I currently work or volunteer under supervision in a home- or community-based setting. If I am a Community Health Worker (CHW), I acknowledge that I must work or volunteer under supervision in a home-based (not community-based) setting to be eligible for a Level 3 incentive.',
        props: { variant: 'largeBody' },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'yes'),
  },
  attestationToSeekAJob: {
    key: 'attestationToSeekAJob',
    copy: { title: 'Attestation' },
    fields: [
      {
        key: 'attestationToSeekAJob',
        type: FieldType.Typography,
        copy: 'By tapping CONTINUE, I acknowledge that I intend to seek employment or supervised volunteer opportunities in a home- or community-based setting. If I am a Community Health Worker (CHW), I acknowledge that I must work or volunteer under supervision in a home-based (not community-based) setting to be eligible for a Level 3 incentive.',
        props: { variant: 'largeBody' },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'no'),
  },
  licenses: {
    key: 'licenses',
    copy: {
      title: 'Upload proof of documents',
      intro: `
Proof of current qualifying California-issued licenses/certifications. Note: course completion certificates do not qualify as proof. Go [here](https://www.caring4cal.org/en/faq/#eligibility) to view Caring4Cal Incentives FAQs, including what documents qualify as proof.

OR
    
Proof of current active employment or supervised volunteering as an eligible home-based CHW. Please have your employer(s) complete this [employment / volunteering verification form](${VERIFICATION_FORM_LINK}) to show proof of current employment or supervised volunteering as a home-based CHW. 

Use the Upload button to add your form(s).`,
    },
    fields: [
      {
        key: 'documents.license',
        type: FieldType.Document,
      },
    ],
  },
  proofOfEligibilityType: {
    key: 'proofOfEligibilityType',
    copy: {
      title: 'Choose your proof of Level 3 eligibility',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'proofOfEligibilityType',
        options: [
          {
            label: 'Proof of employment or supervised volunteering in eligible job setting',
            value: 'employment',
          },
          {
            label: 'Proof of additional earned license or certification',
            value: 'certification',
          },
        ],
      },
    ],
  },
  employers: mergeObjects(
    [
      COMMON_QUESTIONS.employers,
      {
        copy: {
          title: 'Proof of Employment / Supervised Volunteering Information',
          intro: `
Please list all of your employers who can verify your employment or supervised volunteering for this incentive claim.

For each employer, add their name and zip code. Please also have your employer(s) complete this [employment verification form](${VERIFICATION_FORM_LINK}) to show proof of 40 hours of work/month in California for 4 months or more since the beginning of the Caring4Cal program, with the most recent month in a qualifying job setting. 

If you have more than one employer, have each employer complete the [employment verification form](${VERIFICATION_FORM_LINK}). Use the Upload button to add your completed form(s).`,
        },
        fields: [
          {
            subFields: [
              undefined,
              undefined,
              {
                type: FieldType.Document,
                key: 'employmentProof',
              },
            ],
          },
        ],
        dynamicLogic: eq('proofOfEligibilityType', 'employment'),
      },
    ],
    'merge',
  ) as ApplicationQuestion,
  certifications: {
    key: 'certifications',
    copy: {
      title: 'Proof of Additional License/Certification',
      intro: `
Upload proof of newly earned license and/or certification. New license/certification must be earned since November 1, 2023 to be eligible. 

Note: course completion certificates do not qualify as proof. Go [here](https://www.caring4cal.org/en/faq/#eligibility) to view Caring4Cal Incentives FAQs, including what documents qualify as proof.  

I agree that I intend to work in an eligible home- or community-based setting.`,
    },
    fields: [
      {
        copy: { title: 'Documents' },
        type: FieldType.Document,
        key: 'documents.certification',
      },
    ],
    dynamicLogic: eq('proofOfEligibilityType', 'certification'),
  },
  surveyLevel1Group1: {
    key: 'surveyLevel1Group1',
    copy: {
      title: 'Please rate the following statements.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.able-to-find-and-take-training-courses-fit-my-interests',
        copy: 'I was able to find and take training courses that fit my interests.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.satisfied-with-training-you-participated-in',
        copy: 'How satisfied are you with the training you participated in?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.without-this-program-not-have-taken-trainings',
        copy: 'Without this program, I would not have taken trainings like these at this time.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.courses-were-relevant-to-skills',
        copy: 'The courses I took were relevant to the skills I need in my career as a care provider.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.satisfied-with-the-coaching-services',
        copy: 'If you received texts from or had a call with a coach, how satisfied are you with the coaching services you received?',
        options: [
          ...SatisfiedOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
    ],
  },
  surveyLevel1Group2: {
    key: 'surveyLevel1Group2',
    copy: {
      title: 'Please rate the following statements about your participation so far in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.participation-improved-the-care-i-provide-to-patients',
        copy: 'My participation in Caring4Cal has helped me to improve the care I provide to patients and clients.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.confident-in-my-ability-to-provide-high-quality-care',
        copy: 'After the training you took, how confident do you feel today in your ability to provide high quality care to your patients and clients?',
        options: ConfidentOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.better-provide-care-to-patients-from-diverse-backgrounds',
        copy: 'After the training I took, I can better provide care to patients and clients from diverse backgrounds.',
        options: AgreementOptions,
        validation: { required: false },
      },
    ],
  },
  surveyLevel1Group3: {
    key: 'surveyLevel1Group3',
    copy: {
      title: 'Please rate the following statements about about your care providing career.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.satisfied-with-your-career',
        copy: 'How satisfied do you feel with your career?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.likely-working-as-care-provider-next-12-months',
        copy: 'How likely are you to continue working as a care provider for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'no'),
  },
  surveyLevel1HEJGroup3: {
    key: 'surveyLevel1HEJGroup3',
    copy: {
      title: 'Please rate the following statements about about your care providing career.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.satisfied-with-your-career',
        copy: 'How satisfied do you feel with your career?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.more-able-to-continue-to-work-in-my-current-job',
        copy: 'I feel more able to continue to work in my current job(s) than I did before participating in Caring4Cal.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.likely-working-as-care-provider-next-12-months',
        copy: 'How likely are you to continue working as a care provider for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel1.likely-working-in-home-community-based-settings-next-12-months',
        copy: 'How likely are you to continue working in home- or community-based settings for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'yes'),
  },
  surveyLevel2Group1: {
    key: 'surveyLevel2Group1',
    copy: {
      title: 'Please rate the following statements about the training you took.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.able-to-find-and-take-training-courses-fit-my-interests',
        copy: 'I was able to find and take training courses that fit my interests.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.satisfied-with-training-you-participated-in',
        copy: 'How satisfied are you with the training you participated in?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.without-this-program-not-have-taken-trainings',
        copy: 'Without this program, I would not have taken trainings like these at this time.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.courses-were-relevant-to-skills',
        copy: 'The courses I took were relevant to the skills I need in my career as a care provider.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.learned-new-skills-that-are-important-to-my-work-as-care-provider',
        copy: 'I learned new skills that are important to my work as a care provider.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.satisfied-with-the-coaching-services',
        copy: 'If you received texts from, or had a call with a coach, how satisfied are you with the coaching services you received?',
        options: [
          ...SatisfiedOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'no'),
  },
  surveyLevel2Group2: {
    key: 'surveyLevel2Group2',
    copy: {
      title: 'Please rate the following statements about your participation so far in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.participation-improved-the-care-i-provide-to-patients',
        copy: 'My participation in Caring4Cal has helped me to improve the care I provide to patients and clients.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.confident-in-my-ability-to-provide-high-quality-care',
        copy: 'After the training you took, how confident do you feel today in your ability to provide high quality care to your patients and clients?',
        options: ConfidentOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.better-provide-care-to-patients-from-diverse-backgrounds',
        copy: 'After the training I took, I can better provide care to patients and clients from diverse backgrounds.',
        options: AgreementOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'no'),
  },
  surveyLevel2Group3: {
    key: 'surveyLevel2Group3',
    copy: {
      title: 'Please rate the following statements about about your care providing career.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.courses-will-advance-my-career',
        copy: 'The courses I took will help me advance in my career.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.communication-with-coach-will-advance-my-career',
        copy: 'If you received texts from, or had a call with a coach, how much do you agree with the following statement, "The support my coach gave me will help me advance in my career."?',
        options: [
          ...AgreementOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.likely-working-as-care-provider-next-12-months',
        copy: 'How likely are you to continue working as a care provider for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'no'),
  },
  surveyLevel2HEJGroup1: {
    key: 'surveyLevel2HEJGroup1',
    copy: {
      title: 'Please rate the following statements about the training you took.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.able-to-find-and-take-training-courses-fit-my-interests',
        copy: 'I was able to find and take training courses that fit my interests.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.satisfied-with-training-you-participated-in',
        copy: 'How satisfied are you with the training you participated in?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.without-this-program-not-have-taken-trainings',
        copy: 'Without this program, I would not have taken trainings like these at this time.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.courses-were-relevant-to-skills',
        copy: 'The courses I took were relevant to the skills I need in my career as a care provider.',
        options: AgreementOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'yes'),
  },
  surveyLevel2HEJGroup2: {
    key: 'surveyLevel2HEJGroup2',
    copy: {
      title: 'Please rate the following statements about your participation so far in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.participation-improved-the-care-i-provide-to-patients',
        copy: 'My participation in Caring4Cal has helped me to improve the care I provide to patients and clients.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.confident-in-my-ability-to-provide-high-quality-care',
        copy: 'After the training you took, how confident do you feel today in your ability to provide high quality care to your patients and clients?',
        options: ConfidentOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.better-provide-care-to-patients-from-diverse-backgrounds',
        copy: 'After the training I took, I can better provide care to patients and clients from diverse backgrounds.',
        options: AgreementOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'yes'),
  },
  surveyLevel2HEJGroup3: {
    key: 'surveyLevel2HEJGroup3',
    copy: {
      title: 'Please rate the following statements about about your care providing career.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.satisfied-with-your-career',
        copy: 'How satisfied do you feel with your career?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.more-able-to-continue-to-work-in-my-current-job',
        copy: 'I feel more able to continue to work in my current job(s) than I did before participating in Caring4Cal.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.learned-new-skills-that-are-important-to-my-work-as-care-provider',
        copy: 'I learned new skills that are important to my work as a care provider.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.courses-will-advance-my-career',
        copy: 'The courses I took will help me advance in my career.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.likely-working-as-care-provider-next-12-months',
        copy: 'How likely are you to continue working as a care provider for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.likely-working-in-home-community-based-settings-next-12-months',
        copy: 'How likely are you to continue working in home- or community-based settings for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'yes'),
  },
  surveyLevel2HEJGroup4: {
    key: 'surveyLevel2HEJGroup4',
    copy: {
      title: 'Please rate the following statements about about your care providing career.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.communication-with-coach-will-advance-my-career',
        copy: 'If you received texts from, or had a call with a coach, how much do you agree with the following statement, "The support my coach gave me will help me advance in my career."?',
        options: [
          ...AgreementOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel2.satisfied-with-the-coaching-services',
        copy: 'If you received texts from, or had a call with a coach, how satisfied are you with the coaching services you received?',
        options: [
          ...SatisfiedOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'yes'),
  },
  surveyLevel3Group1: {
    key: 'surveyLevel3Group1',
    copy: {
      title: 'Please rate the following statements based on your participation in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.learned-new-skills-that-are-important-to-my-work-as-care-provider',
        copy: 'I learned new skills that are important to my work as a care provider.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.satisfied-with-the-coaching-services',
        copy: 'If you received texts from, or had a call with a coach, how satisfied are you with the coaching services you received?',
        options: [
          ...SatisfiedOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.communication-with-coach-will-advance-my-career',
        copy: 'If you received texts from, or had a call with a coach, how much do you agree with the following statement, "The support my coach gave me will help me advance in my career."?',
        options: [
          ...AgreementOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'no'),
  },
  surveyLevel3Group2: {
    key: 'surveyLevel3Group2',
    copy: {
      title: 'Please rate the following statements based on your participation in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.participation-improved-the-care-i-provide-to-patients',
        copy: 'My participation in Caring4Cal has helped me to improve the care I provide to patients and clients.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.confident-in-my-ability-to-provide-high-quality-care',
        copy: 'How confident do you feel today in your ability to provide high quality care to your patients and clients?',
        options: ConfidentOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.better-provide-care-to-patients-from-diverse-backgrounds',
        copy: 'I can better provide care to patients and clients from diverse backgrounds.',
        options: AgreementOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'no'),
  },
  surveyLevel3Group3: {
    key: 'surveyLevel3Group3',
    copy: {
      title: 'Please rate the following statements based on your participation in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.courses-have-helped-me-advance-my-career',
        copy: 'The courses I took have helped me advance in my career.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.satisfied-with-career-as-care-provider',
        copy: 'In your new role as a care provider in a home- or community-based setting, how satisfied do you feel with your career today?',
        options: [
          ...SatisfiedOptions,
          {
            value: 'have-not-started-a-new-role',
            label: 'I have not started a new role',
          },
        ],
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.likely-working-as-care-provider-next-12-months',
        copy: 'How likely are you to continue working as a care provider for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.likely-working-in-home-community-based-settings-next-12-months',
        copy: 'How likely are you to continue working in home- or community-based settings for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'no'),
  },
  surveyLevel3HEJGroup1: {
    key: 'surveyLevel3HEJGroup1',
    copy: {
      title: 'Please rate the following statements based on your participation in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.learned-new-skills-that-are-important-to-my-work-as-care-provider',
        copy: 'I learned new skills that are important to my work as a care provider.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.courses-have-helped-me-advance-my-career',
        copy: 'The courses I took have helped me advance in my career.',
        options: AgreementOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'yes'),
  },
  surveyLevel3HEJGroup2: {
    key: 'surveyLevel3HEJGroup2',
    copy: {
      title: 'Please rate the following statements based on your participation in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.participation-improved-the-care-i-provide-to-patients',
        copy: 'My participation in Caring4Cal has helped me to improve the care I provide to patients and clients.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.confident-provide-high-quality-care',
        copy: 'How confident do you feel TODAY in your ability to provide high quality care to your patients and clients?',
        options: ConfidentOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.better-provide-care-to-patients-from-diverse-backgrounds',
        copy: 'I can better provide care to patients and clients from diverse backgrounds.',
        options: AgreementOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'yes'),
  },
  surveyLevel3HEJGroup3: {
    key: 'surveyLevel3HEJGroup3',
    copy: {
      title: 'Please rate the following statements based on your participation in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.satisfied-with-your-career',
        copy: 'How satisfied do you feel with your career?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.planning-working-in-my-current-jobs-next-12-months',
        copy: 'I plan to continue working in my current job(s) for at least the next 12 months.',
        options: AgreementOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.likely-working-as-care-provider-next-12-months',
        copy: 'How likely are you to continue working as a care provider for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.likely-working-in-home-community-based-settings-next-12-months',
        copy: 'How likely are you to continue working in home- or community-based settings for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'yes'),
  },
  surveyLevel3HEJGroup4: {
    key: 'surveyLevel3HEJGroup4',
    copy: {
      title: 'Please rate the following statements based on your participation in Caring4Cal.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.satisfied-with-the-coaching-services',
        copy: 'If you received texts from, or had a call with a coach, how satisfied are you with the coaching services you received?',
        options: [
          ...SatisfiedOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyLevel3.communication-with-coach-will-advance-my-career',
        copy: 'If you received texts from, or had a call with a coach, how much do you agree with the following statement, "The support my coach gave me has helped me advance in my career."?',
        options: [
          ...AgreementOptions,
          {
            value: 'did-not-receive-coaching-services',
            label: 'I didn’t receive coaching services',
          },
        ],
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('workInAnEligibleJob', 'yes'),
  },
};

const SECTIONS: ApplicationSection[] = [
  {
    key: 'employmentInformationSection',
    name: 'Employment / Supervised Volunteering Information',
    overview: {
      description: `In this section we'll gather some information about your employment or supervised volunteering.`,
      title: 'Employment / Supervised Volunteering Information',
    },
    questionGroups: [
      {
        key: 'employmentInformationSection',
        name: 'Employment / Supervised Volunteering Information',
        questions: [
          QUESTIONS.workInAnEligibleJob,
          QUESTIONS.attestationOfHavingAJob,
          QUESTIONS.attestationToSeekAJob,
          QUESTIONS.licenses,
        ],
      },
    ],
  },
  {
    key: 'incentiveLevelSelection',
    name: 'Incentive Level Selection',
    overview: {
      description: `
You will complete a full application for each incentive payment request. Once you complete each application, you will be eligible to complete and submit another incentive payment request.
  
**Participants may claim each incentive level once in EITHER Track 1 OR Track 2. Do not submit multiple applications for the same level in the same or different tracks.**`,
      title: 'Incentive Level Selection',
    },
    questionGroups: [
      {
        key: 'firstTimeApplying',
        name: 'First Time Applying',
        questions: [COMMON_QUESTIONS.firstTimeApplying],
      },
      {
        key: 'incentiveLevelsGroup',
        name: 'Incentive Level Selection',
        dynamicLogic: eq('firstTimeApplying', 'no'),
        questions: [COMMON_QUESTIONS.incentiveLevels],
      },
      {
        key: 'incentiveLevel1',
        name: "You selected 'Level 1: Jump Start'",
        overview: {
          title: "You selected 'Level 1: Jump Start'",
          description: `
You need at least 15 hours of Caring4Cal training to claim this incentive.

**You may claim Level 1 only once.**`,
        },
        dynamicLogic: or([eq('incentiveLevel', 'level1'), eq('firstTimeApplying', 'yes')]),
        questions: [
          COMMON_QUESTIONS.courseCompletedL1,
          {
            key: 'surveyQuestionLevel1',
            copy: {
              title:
                'We’d like to ask you a few questions about your experience so far with Caring4Cal.',
            },
            fields: [],
          },
          QUESTIONS.surveyLevel1Group1,
          QUESTIONS.surveyLevel1Group2,
          QUESTIONS.surveyLevel1Group3,
          QUESTIONS.surveyLevel1HEJGroup3,
        ],
      },
      {
        key: 'incentiveLevel2',
        name: "You selected 'Level 2: Leap Forward'",
        overview: {
          title: "You selected 'Level 2: Leap Forward'",
          description: `
You need at least 15 hours of additional Caring4Cal Training to claim Level 2 and must have claimed Level 1.

**You may claim Level 2 only once.**`,
        },
        dynamicLogic: eq('incentiveLevel', 'level2'),
        questions: [
          COMMON_QUESTIONS.courseCompletedL2,
          {
            key: 'surveyQuestionLevel2',
            copy: {
              title:
                'We’d like to ask you a few questions about your experience so far with Caring4Cal.',
            },
            fields: [],
          },
          QUESTIONS.surveyLevel2Group1,
          QUESTIONS.surveyLevel2Group2,
          QUESTIONS.surveyLevel2Group3,
          QUESTIONS.surveyLevel2HEJGroup1,
          QUESTIONS.surveyLevel2HEJGroup2,
          QUESTIONS.surveyLevel2HEJGroup3,
          QUESTIONS.surveyLevel2HEJGroup4,
        ],
      },
      {
        key: 'incentiveLevel3',
        name: "You selected 'Level 3: Retention & Recruitment'",
        overview: {
          title: "You selected 'Level 3: Retention & Recruitment'",
          description: `
You must have claimed Levels 1 and 2 to be eligible to claim this level. 

**You may claim Level 3 only once.**`,
        },
        dynamicLogic: eq('incentiveLevel', 'level3'),
        questions: [
          QUESTIONS.proofOfEligibilityType,
          QUESTIONS.employers,
          QUESTIONS.certifications,
          {
            key: 'surveyQuestionLevel3',
            copy: {
              title: 'We’d like to ask you a few questions about your experience with Caring4Cal.',
            },
            fields: [],
          },
          QUESTIONS.surveyLevel3Group1,
          QUESTIONS.surveyLevel3Group2,
          QUESTIONS.surveyLevel3Group3,
          QUESTIONS.surveyLevel3HEJGroup1,
          QUESTIONS.surveyLevel3HEJGroup2,
          QUESTIONS.surveyLevel3HEJGroup3,
          QUESTIONS.surveyLevel3HEJGroup4,
        ],
      },
      {
        key: CommonQuestionKeys.ADDRESS,
        name: 'Home Address',
        questions: [COMMON_QUESTIONS[CommonQuestionKeys.ADDRESS]],
      },
      {
        key: CommonQuestionKeys.MAILING_ADDRESS,
        name: 'Mailing Address',
        questions: [COMMON_QUESTIONS[CommonQuestionKeys.MAILING_ADDRESS]],
      },
    ],
  },
  COMMON_SECTIONS.paymentInfoSection,
  COMMON_SECTIONS.review,
];
const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'Welcome to Caring4Cal!',
      body: `
Please read the following carefully.  
  
**You chose this track because you DO have a current California-issued license or certification in an eligible job role or are a CHW.**

Not sure if you have an eligible job role? Review eligibility guidelines [here](https://www.caring4cal.org/en/eligibility/) or call (844) 425-1516.
  
If you DO meet the eligibility requirements above, click CONTINUE. **If you DO NOT, please go back and select Track 1.**`,
    },
    COMMON_PAGES.applicationProcess,
    COMMON_PAGES.termsOfServiceAndPrivacyPolicy,
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: '',
        message: `
**Do Have a Current License or Certification or a role as a CHW**

Please be sure to complete your Caring4Cal Registration before claiming incentives.

**Are you in the right track?**

**Choose this track if you are a CHW or already have a current California-issued license or certification. Please review eligibility guidelines [here](https://www.caring4cal.org/en/eligibility) or call ${formatIfPhone(
          '+18444251516',
        )} to confirm.**

You may claim each incentive level only once, regardless of which track you choose.`,
      },
      {
        status: 'Incomplete',
        message:
          'You’ve made great progress so far! Keep working on your Caring4Cal incentive application.',
      },
      {
        status: 'InProgress',
        message:
          'You’ve made great progress so far! Keep working on your Caring4Cal incentive application.',
      },
      {
        status: 'AwaitingReview',
        message:
          'Thanks for submitting your application! Your application will be reviewed in the order it was received. We will contact you if we have any questions. Check your email or back here for updates.',
      },
      {
        status: 'InReview',
        message:
          'We’re currently reviewing your application and will reach a decision shortly. You’ll receive an email once a decision has been made and no further action is required on your part.',
      },
      {
        status: 'Denied',
        message:
          'Your application was denied. Please check the email that was sent to you for more information.',
      },
    ],
    submission: COMMON_OVERRIDES.submission,
  },
  sections: SECTIONS,
};
export default CONFIG;
